import { Button, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { RentalCustomer } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import { RentalCustomerModal } from "./rental-customer-modal";


type RentalCustomersProps = {
  rentalCustomers?: RentalCustomer[],
  onRentalCustomerSelected: (a: RentalCustomer) => void
}

const RentalCustomers = memo(({ rentalCustomers, onRentalCustomerSelected }: RentalCustomersProps) => {
  const { sortedList, sortData } = useSort(rentalCustomers, "companyName");

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["username", "companyName", "firstName", "lastName", "phone", "email", "comment",]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Customers" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="companyName" sortData={sortData} >
                Company
              </SortableHeader>

              <SortableHeader sortKey="firstName" sortData={sortData} >
                First Name
              </SortableHeader>

              <SortableHeader sortKey="lastName" sortData={sortData} >
                Last Name
              </SortableHeader>

              <SortableHeader sortKey="username" sortData={sortData} >
                Username
              </SortableHeader>

              <SortableHeader sortKey="phone" sortData={sortData} >
                Phone
              </SortableHeader>

              <SortableHeader sortKey="email" sortData={sortData} >
                Email
              </SortableHeader>

              <SortableHeader sortKey="comment" sortData={sortData} >
                Comment
              </SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((rentalCustomer, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onRentalCustomerSelected(rentalCustomer)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                {rentalCustomer.companyName}
              </TruxTableCell>
              <TruxTableCell>
                {rentalCustomer.firstName}
              </TruxTableCell>
              <TruxTableCell>
                {rentalCustomer.lastName}
              </TruxTableCell>
              <TruxTableCell>
                {rentalCustomer.username}
              </TruxTableCell>
              <TruxTableCell>
                {rentalCustomer.phone}
              </TruxTableCell>
              <TruxTableCell>
                {rentalCustomer.email}
              </TruxTableCell>
              <TruxTableCell>
                {rentalCustomer.comment}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  rentalCustomers?: RentalCustomer[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    rentalCustomers: axios.get(endpoints.rentalCustomers.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);

  const selectedRentalCustomerModel = useModelSavingData<RentalCustomer>(
    endpoints.rentalCustomers.update,
    endpoints.rentalCustomers.create
  );

  const updateRentalCustomer = () => {
    selectedRentalCustomerModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Customer");
        const rentalCustomer = response.data as RentalCustomer;

        if (!rentalCustomer.active) {
          updateData("rentalCustomers")(deleteById(data!.rentalCustomers!, rentalCustomer))
        } else {
          updateData("rentalCustomers")(replaceById(data!.rentalCustomers!, rentalCustomer));
        }

        setIsOpen(false);
      }
    })
  }

  const onRentalCustomerSelected = useCallback((a: RentalCustomer) => {
    selectedRentalCustomerModel.set({
      ...a
    });
    setIsOpen(true);
  }, [selectedRentalCustomerModel, setIsOpen]);


  const onNewRentalCustomerSelected = useCallback(() => {
    selectedRentalCustomerModel.set({
      id: 0,
      createdAt: undefined,
      active: true,
      organizationId: 0,

      username: "",
      password: "",

      companyName: "",
      firstName: "",
      lastName: "",
      dotNumber: "",
      feinNumber: "",
      mcNumber: "",
      address: {
        attention: "",
        street1: "",
        street2: "",
        city: "",
        state: "",
        zip: "",
      },
      phone: "",
      email: "",
      comment: "",
    });
    setIsOpen(true);
  }, [selectedRentalCustomerModel]);

  const newRentalCustomerButton = <Button
    onClick={onNewRentalCustomerSelected}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    New Customer
  </Button>;

const inactiveRentalCustomersButton = <Button
  onClick={() => router.push("/app/rentals/customers/inactive")}
  variant="soft"
>
  Inactive Customers
</Button>;


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.rentalCustomers?.length !== 0 &&
          <PageHeader title="Customers">
            {inactiveRentalCustomersButton}
            {newRentalCustomerButton}
          </PageHeader>
        }

        {data && data?.rentalCustomers?.length === 0 &&
          <NoneFound title="No Active Customers Found" message="To add an Customer click the 'New Customer' button below.">
            {inactiveRentalCustomersButton}
            {newRentalCustomerButton}
          </NoneFound>
        }

        {data && data?.rentalCustomers?.length !== 0 && <RentalCustomers rentalCustomers={data.rentalCustomers} onRentalCustomerSelected={onRentalCustomerSelected} />}

        {data && <RentalCustomerModal
          selectedRentalCustomerModel={selectedRentalCustomerModel}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          updateRentalCustomer={updateRentalCustomer}
        />}
      </DisplayPageData>
    </Container >
  );
}
